import {
    computed,
    DOCUMENT,
    effect,
    EnvironmentProviders,
    inject,
    Injectable,
    InjectionToken,
    isDevMode,
    LOCALE_ID,
    makeEnvironmentProviders,
    Provider,
    Signal,
    Type,
} from '@angular/core';
import { Language } from '@cirklo/client';
import { provideTransloco, provideTranslocoLoader, TranslocoLoader, TranslocoService } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { MemoizedSelector, Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import {
    DEFAULT_LANGUAGE,
    getBrowserLanguage,
    getLocale,
    SUPPORTED_LANGUAGES,
    SUPPORTED_LOCALES,
} from './get-supported-language';

const LANGUAGE_SERVICE_LANGUAGE_SUBJECT = new InjectionToken<MemoizedSelector<Record<string, unknown>, Language>>(
    'LanguageServiceFeature',
);
const initialLanguage = getBrowserLanguage();

export function provideLanguageService(config: {
    selector: MemoizedSelector<Record<string, unknown>, Language>;
    loader: Type<TranslocoLoader>;
}) {
    const providers: (Provider | EnvironmentProviders)[] = [
        { provide: LANGUAGE_SERVICE_LANGUAGE_SUBJECT, useValue: config.selector },
        // Try to avoid using LOCALE_ID whenever possible as it cannot be updated at runtime,
        // use TranslocoLocaleService#getLocale instead.
        // We're still setting it to ensure matDatepicker shows the correct month names
        { provide: LOCALE_ID, useValue: getLocale(initialLanguage.toLowerCase()) },
        provideTransloco({
            config: {
                availableLangs: SUPPORTED_LANGUAGES.map((l) => l.toLowerCase()),
                defaultLang: initialLanguage.toLowerCase(),
                fallbackLang: DEFAULT_LANGUAGE.toLowerCase(),
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
        }),
        provideTranslocoLoader(config.loader),
        provideTranslocoLocale({
            langToLocaleMapping: {
                [Language.EN.toLowerCase()]: 'en-GB',
                [Language.NL.toLowerCase()]: 'nl-BE',
                [Language.FR.toLowerCase()]: 'fr-FR',
            },
            defaultCurrency: 'EUR',
            localeToCurrencyMapping: Object.fromEntries(SUPPORTED_LOCALES.map((lang) => [lang.toLowerCase(), 'EUR'])),
        }),
    ];
    return makeEnvironmentProviders(providers);
}

@Injectable({ providedIn: 'root' })
export class LanguageService {
    languages: { name: string; code: Language }[] = [
        { name: 'Nederlands', code: Language.NL },
        { name: 'Français', code: Language.FR },
        { name: 'English', code: Language.EN },
    ];
    private readonly transloco = inject(TranslocoService);
    private readonly store = inject(Store);
    private readonly languageSelector = inject(LANGUAGE_SERVICE_LANGUAGE_SUBJECT);
    private readonly document = inject(DOCUMENT);
    language: Signal<Language> = this.store.selectSignal(this.languageSelector);
    lowercaseLanguage = computed(() => this.language().toLowerCase());

    constructor() {
        effect(() => {
            const language = this.lowercaseLanguage();
            this.transloco.setActiveLang(language);
            this.document.documentElement.lang = language;
        });
    }

    /**
     * Must be called from the provideAppInitializer function to ensure the correct language is set and loaded.
     */
    initialize() {
        return firstValueFrom(this.transloco.load(this.lowercaseLanguage()));
    }
}
